import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useFacebookLogin,
  useVote,
  UseVoteAPIResponseDTO,
  useWidget,
  VoteAPIResponseCode,
} from '@telescope/cassini-hooks';
import { useStorage } from 'providers';
import React, { useCallback, useMemo } from 'react';
import { ErrorType } from 'types';
import { getEmailLoginPayload, getFBLoginPayload } from '../lib';
import { AuthMethod, AuthType, AUTH_LOCALSTORAGE_KEY } from '../utils/constants';
import { isTrue, areTargetingCookiesAllowed } from 'utils';
import { Open, Closed } from 'generated-types/widget';

// TODO: Replace 'any' with correct typings for auth function payloads
export interface AuthProviderConfig<User = unknown, Error = unknown> {
  key?: string;
  waitInitial?: boolean;
  LoaderComponent?: () => JSX.Element;
  ErrorComponent?: ({ error }: { error: Error | null }) => JSX.Element;
}

export interface AuthContextValue<
  User = unknown,
  Error = unknown,
  LoginCredentials = unknown,
  RegisterCredentials = any
> {
  user: User | undefined;
  loginWithEmail: UseMutateAsyncFunction<User, any, LoginCredentials>;
  loginWithFacebook: UseMutateAsyncFunction<User, any, LoginCredentials>;
  logout: UseMutateAsyncFunction<any, any, void, any>;
  registerWithEmail: UseMutateAsyncFunction<User, any, RegisterCredentials>;
  registerWithFacebook: UseMutateAsyncFunction<User, any, RegisterCredentials>;
  updateUserVoteHistory: UseMutateAsyncFunction<User, any, any>;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isRegistering: boolean;
  refetchUser: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<User, Error>>;
  error: Error | null;
  authMethods: Record<AuthMethod, boolean>;
  isMethodEnabled: (method: AuthMethod) => boolean;
  hasRegistration: boolean;
}

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function initReactQueryAuth<
  User = unknown,
  Error = unknown,
  LoginCredentials = unknown,
  RegisterCredentials = unknown
>(config: AuthProviderConfig<User, Error>) {
  const AuthContext = React.createContext<AuthContextValue<User, Error, LoginCredentials, RegisterCredentials> | null>(
    null
  );
  AuthContext.displayName = 'AuthContext';

  const {
    key = 'auth-user',
    waitInitial = true,
    LoaderComponent = () => <div>Loading...</div>,
    ErrorComponent = (error: any) => <div style={{ color: 'tomato' }}>{JSON.stringify(error, null, 2)}</div>,
  } = config;

  function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const storage = useStorage();
    const loginTelescope = useVote();
    const { login: loginFB } = useFacebookLogin(
      (e) => console.log('FB Login success', e),
      () => console.log('Logout'),
      () => console.log('FB Error')
    );

    const queryClient = useQueryClient();
    const { data: widget} = useWidget({ select: (data: Open | Closed) => data });
    const { sid } = widget!
    const { data: rulesetParameters } = useWidget({ 
      select: (data: Open | Closed) => data.widget.settings.bingoSettings.ruleset_parameters  });
    const { hasRegistration, hasOIDC } = rulesetParameters!;

    const { data: authSettings } = useWidget({ select: (data: Open) => data.widget.global_views.auth.settings });
    const { isFacebookLoginEnabled, isEmailLoginEnabled, fbAppId, requestEmailScope } = authSettings!;
    const authMethods = useMemo(
      () => ({
        [AuthMethod.FACEBOOK]: !!(isFacebookLoginEnabled && fbAppId && areTargetingCookiesAllowed()),
        [AuthMethod.EMAIL]: isEmailLoginEnabled,
        [AuthMethod.OAUTH]: !!hasOIDC,
      }),
      [
        fbAppId,
        hasOIDC,
        isEmailLoginEnabled,
        isFacebookLoginEnabled,
      ]
    );

    const loadUser = async (): Promise<User> => {
      return storage.getItem(AUTH_LOCALSTORAGE_KEY);
    };

    const logoutFn = async () => {
      return storage.removeItem(AUTH_LOCALSTORAGE_KEY);
    };

    const updateVoteHistory = async ({ submitted } : { submitted?: boolean }) => {
      return { ...user, submitted } as User;
    };

    const loginWithEmail = async (payload: any) => {
      const emailLoginPayload = getEmailLoginPayload(payload);

      // Step 1 - Authenticate user
      const { response_code: loginResCode } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.LOGIN,
      })) as UseVoteAPIResponseDTO;

      if (loginResCode !== VoteAPIResponseCode.VALID && loginResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(loginResCode);
      }

      // Step 2 - Enter user on sweeps and get prize data
      const { response_code: sweepsResCode, submitted, is_winner, prize_data, ...prize } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.SWEEPS,
      })) as UseVoteAPIResponseDTO;

      if (sweepsResCode !== VoteAPIResponseCode.VALID && sweepsResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(sweepsResCode);
      }

      const userData = { 
        user: emailLoginPayload, 
        sid, 
        isWinner: isTrue(is_winner), 
        submitted: submitted === 'true',
        prize: {
          ...prize,
          ...(prize_data && {prize_data: JSON.parse(prize_data)})
        }
      };
      return userData as User;
    };

    const registerWithEmail = async ({
      payload,
      checkIfUserAlreadyRegistered,
    }: {
      payload: any;
      checkIfUserAlreadyRegistered: boolean;
    }) => {
      const emailLoginPayload = getEmailLoginPayload(payload);

      // Step 1 in registration is to check if the user already exists. We don't want to save the user in case the registration has additional fields, so already reject the promise
      if (checkIfUserAlreadyRegistered) {
        const { response_code: isRegisteredResCode } = (await loginTelescope({
          ...emailLoginPayload,
          action_type: AuthType.LOGIN,
        })) as UseVoteAPIResponseDTO;

        const isUserAlreadyRegistered = isRegisteredResCode === VoteAPIResponseCode.VALID || isRegisteredResCode === VoteAPIResponseCode.OVERLIMIT;
        return { user: emailLoginPayload, sid, isUserAlreadyRegistered } as User;
      }

      // Step 2 - Register the user. This will include any additional required form fields
      const { response_code: registrationResCode } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.REGISTER,
      })) as UseVoteAPIResponseDTO;

      if (registrationResCode !== VoteAPIResponseCode.VALID) {
        return Promise.reject(new Error(AuthType.REGISTER));
      }

      // Step 3 - When registration is complete, enter user on sweeps to get prize data and enter the game
      const { response_code: sweepsResCode, submitted, is_winner, prize_data, ...prize } = (await loginTelescope({
        ...emailLoginPayload,
        action_type: AuthType.SWEEPS,
      })) as UseVoteAPIResponseDTO;

      if (sweepsResCode !== VoteAPIResponseCode.VALID && sweepsResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(new Error(AuthType.SWEEPS));
      }

      const userData = { 
        user: emailLoginPayload, 
        sid, 
        isWinner: isTrue(is_winner), 
        submitted: submitted === 'true',
        prize: {
          ...prize,
          ...(prize_data && {prize_data: JSON.parse(prize_data)})
        }
      };

      return userData as User;
    };

    const loginWithFacebook = async (payload: any) => {
      const { user: facebookUser } = await loginFB(window.location.pathname, window.location.pathname, {
        ...(requestEmailScope && { scope: 'email' }),
      });

      if (!facebookUser) {
        return Promise.reject(new Error(ErrorType.FB_GENERIC));
      }

      if (requestEmailScope && !facebookUser.email) {
        return Promise.reject(new Error(ErrorType.FB_PERMISSIONS));
      }
      const fbLoginPayload = getFBLoginPayload(payload, facebookUser);

      // Step 1 - Authenticate user
      const { response_code: loginResCode } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.LOGIN,
      })) as UseVoteAPIResponseDTO;

      if (loginResCode !== VoteAPIResponseCode.VALID && loginResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(loginResCode);
      }

      // Step 2 - Enter user on sweeps and get prize data
      const { response_code: sweepsResCode, submitted, is_winner, prize_data, ...prize } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.SWEEPS,
      })) as UseVoteAPIResponseDTO;

      if (sweepsResCode !== VoteAPIResponseCode.VALID && sweepsResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(sweepsResCode);
      }
      
      const userData = { 
        user: fbLoginPayload, 
        sid, 
        isWinner: isTrue(is_winner), 
        submitted: submitted === 'true',
        prize: {
          ...prize,
          ...(prize_data && {prize_data: JSON.parse(prize_data)})
        }
      };

      return userData as User;
    };

    const registerWithFacebook = async ({
      payload,
      checkIfUserAlreadyRegistered,
    }: {
      payload: any;
      checkIfUserAlreadyRegistered: boolean;
    }) => {
      const { user: facebookUser } = await loginFB(window.location.pathname, window.location.pathname, {
        ...(requestEmailScope && { scope: 'email' }),
      });

      if (!facebookUser) {
        return Promise.reject(new Error(ErrorType.FB_GENERIC));
      }

      if (requestEmailScope && !facebookUser.email) {
        return Promise.reject(new Error(ErrorType.FB_PERMISSIONS));
      }
      const fbLoginPayload = getFBLoginPayload(payload, facebookUser);

      // Step 1 in registration is to check if the user already exists.
      if (checkIfUserAlreadyRegistered) {
        const { response_code: isRegisteredResCode } = (await loginTelescope({
          ...fbLoginPayload,
          action_type: AuthType.LOGIN,
        })) as UseVoteAPIResponseDTO;

        const isUserAlreadyRegistered = isRegisteredResCode === VoteAPIResponseCode.VALID;
        return { user: fbLoginPayload, sid, isUserAlreadyRegistered } as User;
      }

      // Step 2 - Register the user. This will include any additional required form fields
      const { response_code: registrationResCode } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.REGISTER,
      })) as UseVoteAPIResponseDTO;

      if (registrationResCode !== VoteAPIResponseCode.VALID) {
        return Promise.reject(new Error(AuthType.REGISTER));
      }

      // Step 3 - When registration is complete, enter user on sweeps to get prize data and enter the game
      const { response_code: sweepsResCode, submitted, is_winner, prize_data, ...prize } = (await loginTelescope({
        ...fbLoginPayload,
        action_type: AuthType.SWEEPS,
      })) as UseVoteAPIResponseDTO;

      if (sweepsResCode !== VoteAPIResponseCode.VALID && sweepsResCode !== VoteAPIResponseCode.OVERLIMIT) {
        return Promise.reject(new Error(AuthType.SWEEPS));
      }

      const userData = { 
        user: fbLoginPayload, 
        sid, 
        isWinner: isTrue(is_winner), 
        submitted: submitted === 'true',
        prize: {
          ...prize,
          ...(prize_data && {prize_data: JSON.parse(prize_data)})
        }
      };

      return userData as User;
    };

    const {
      data: user,
      error,
      status,
      isLoading,
      isSuccess,
      refetch,
    } = useQuery<User, Error>({
      queryKey: [key, { sid }],
      queryFn: loadUser,
    });

    const isMethodEnabled = useCallback(
      (method: AuthMethod): boolean => {
        return !!authMethods[method];
      },
      [authMethods]
    );

    const setUser = React.useCallback(
      (data: User) => {
        queryClient.setQueryData([key, { sid }], data);
        storage.setItem(AUTH_LOCALSTORAGE_KEY, data);
        return user;
      },
      [queryClient, storage, sid]
    );

    const loginWithEmailMutation = useMutation({
      mutationFn: loginWithEmail,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const loginWithFBMutation = useMutation({
      mutationFn: loginWithFacebook,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const registerWithEmailMutation = useMutation({
      mutationFn: registerWithEmail,
      onSuccess: (user: any) => {
        // If isUserAlreadyRegistered exists on the object, then we're just checking if the user exists before continuing to the next step, so we don't save the user yet.
        if (user.isUserAlreadyRegistered !== undefined) return;
        
        setUser(user);
      },
    });

    const registerWithFacebookMutation = useMutation({
      mutationFn: registerWithFacebook,
      onSuccess: (user: any) => {
        // If isUserAlreadyRegistered exists on the object, then we're just checking if the user exists before continuing to the next step, so we don't save the user yet.
        if (user.isUserAlreadyRegistered !== undefined) return;

        setUser(user);
      },
    });

    const logoutMutation = useMutation({
      mutationFn: logoutFn,
      onSuccess: () => {
        queryClient.resetQueries([key]);
      },
    });

    const updateUserMutation = useMutation({
      mutationFn: updateVoteHistory,
      onSuccess: (user) => {
        setUser(user);
      },
    });

    const value = React.useMemo(
      () => ({
        user,
        error,
        refetchUser: refetch,
        loginWithEmail: loginWithEmailMutation.mutateAsync,
        loginWithFacebook: loginWithFBMutation.mutateAsync,
        isLoggingIn: loginWithEmailMutation.isLoading || loginWithFBMutation.isLoading,
        logout: logoutMutation.mutateAsync,
        isLoggingOut: logoutMutation.isLoading,
        registerWithEmail: registerWithEmailMutation.mutateAsync,
        registerWithFacebook: registerWithFacebookMutation.mutateAsync,
        updateUserVoteHistory: updateUserMutation.mutate,
        isRegistering: registerWithEmailMutation.isLoading || registerWithFacebookMutation.isLoading,
        isMethodEnabled,
        authMethods,
        hasRegistration,
      }),
      [
        user,
        error,
        refetch,
        loginWithEmailMutation.mutateAsync,
        loginWithEmailMutation.isLoading,
        loginWithFBMutation.mutateAsync,
        loginWithFBMutation.isLoading,
        logoutMutation.mutateAsync,
        logoutMutation.isLoading,
        registerWithEmailMutation.mutateAsync,
        registerWithEmailMutation.isLoading,
        registerWithFacebookMutation.mutateAsync,
        registerWithFacebookMutation.isLoading,
        updateUserMutation.mutate,
        isMethodEnabled,
        authMethods,
        hasRegistration,
      ]
    );

    if (isSuccess || !waitInitial) {
      return <AuthContext.Provider value={value as any}>{children}</AuthContext.Provider>;
    }

    if (isLoading) {
      return <LoaderComponent />;
    }

    if (error) {
      return <ErrorComponent error={error} />;
    }

    return <div>Unhandled status: {status}</div>;
  }

  function useAuth() {
    const context = React.useContext(AuthContext);
    if (!context) {
      throw new Error(`useAuth must be used within an AuthProvider`);
    }
    return context;
  }

  return { AuthProvider, AuthConsumer: AuthContext.Consumer, useAuth };
}
